/**
 * Paleta compartilhada para dashboards (Assistências, Painel Executivo, etc.).
 * Documentação: docs/CORES.md
 */

export const BRAND = {
  lightGreen: 'var(--brand-light-green, #8fad15)',
  darkGreen: 'var(--brand-dark-green, #1c4220)',
};

export const KPI = {
  primary: '#198754',
  secondary: '#8fad15',
  tertiary: '#6f42c1',
  quaternary: '#fd7e14',
  brand: '#8fad15',
  success: '#198754',
  warning: '#ffc107',
  danger: '#dc3545',
  accent: '#20c997',
};

/** Ordem para múltiplas séries / cards (igual Assistências) */
export const CHART_SERIES_COLORS = [
  KPI.primary,
  KPI.secondary,
  KPI.tertiary,
  KPI.quaternary,
  KPI.accent,
  KPI.danger,
];

export const CHART_GRADIENTS = {
  primary: { light: '#198754', dark: '#146c43' },
  secondary: { light: '#8fad15', dark: '#5d7110' },
  tertiary: { light: '#6f42c1', dark: '#59359a' },
  quaternary: { light: '#fd7e14', dark: '#ca6510' },
  accent: { light: '#20c997', dark: '#1aa179' },
};

/** Chaves usadas nos gráficos de barra do Painel Executivo */
export const chartColors = {
  green: CHART_GRADIENTS.primary,
  lime: CHART_GRADIENTS.secondary,
  simt: CHART_GRADIENTS.primary,
  bimt: CHART_GRADIENTS.secondary,
  cpmt: CHART_GRADIENTS.tertiary,
  eemt: CHART_GRADIENTS.quaternary,
  esmr: CHART_GRADIENTS.tertiary,
  teal: CHART_GRADIENTS.accent,
  blue: CHART_GRADIENTS.secondary,
  purple: CHART_GRADIENTS.tertiary,
  orange: CHART_GRADIENTS.quaternary,
};

export const getChartTextColor = (isDark) => (isDark ? '#adb5bd' : '#666');

export const getChartGridColor = (isDark) => (isDark ? '#ffffff10' : '#00000010');

export const getChartOptions = (isDark) => ({
  responsive: true,
  maintainAspectRatio: false,
  legend: { display: false },
  scales: {
    xAxes: [
      {
        ticks: {
          fontColor: getChartTextColor(isDark),
          fontSize: 11,
        },
        gridLines: { color: getChartGridColor(isDark) },
      },
    ],
    yAxes: [
      {
        ticks: {
          beginAtZero: true,
          fontColor: getChartTextColor(isDark),
          fontSize: 11,
        },
        gridLines: { color: getChartGridColor(isDark) },
      },
    ],
  },
  tooltips: {
    backgroundColor: isDark ? '#1e1e1e' : '#fff',
    titleFontColor: isDark ? '#f8f9fa' : '#333',
    bodyFontColor: isDark ? '#adb5bd' : '#666',
    borderColor: isDark ? '#495057' : '#dee2e6',
    borderWidth: 1,
  },
});

/** Superfície de card alinhada ao theme.css (evita tons slate/azulados) */
export const getDashboardCardSurface = () => ({
  background: 'var(--card-bg)',
  border: '1px solid var(--card-border)',
  boxShadow: 'var(--card-shadow)',
});

/** Variáveis CSS para Tabs Bootstrap (pills) na paleta da marca */
export const getDashboardTabsStyle = (darkMode) => ({
  '--bs-nav-pills-link-active-bg': KPI.primary,
  '--bs-nav-pills-link-active-color': '#ffffff',
  '--bs-nav-link-color': darkMode ? 'var(--brand-light-green)' : 'var(--brand-dark-green)',
});

export const getDashboardShellStyles = (darkMode) => ({
  shell: {
    background: darkMode
      ? 'linear-gradient(135deg, #0a1810 0%, #0c392e 50%, #121a14 100%)'
      : 'linear-gradient(135deg, #eef4ed 0%, #f8faf8 45%, #edf7ef 100%)',
    borderRadius: '24px',
    padding: '18px',
    border: darkMode ? '1px solid rgba(143, 173, 21, 0.12)' : '1px solid rgba(28, 66, 32, 0.08)',
  },
  panel: {
    ...getDashboardCardSurface(),
    borderRadius: '18px',
  },
  mutedText: {
    color: darkMode ? '#adb5bd' : '#6c757d',
  },
  titleText: {
    color: darkMode ? '#f8f9fa' : '#1c4220',
  },
});

export const getKpiAccentByIndex = (index) => {
  const accents = [KPI.primary, KPI.secondary, KPI.tertiary, KPI.quaternary];
  return accents[index % accents.length];
};

/** Ciclo para cards KPI do painel de Assistências */
export const KPI_ACCENT_CYCLE = [KPI.primary, KPI.secondary, KPI.tertiary, KPI.quaternary];

/** Categorias de chaves (lista / detalhe) — paleta verde da marca */
export const ASSISTENCIA_CATEGORY_COLORS = {
  total: '#6c757d',
  contabilizadas: KPI.primary,
  helpDesk: KPI.secondary,
  locSolution: KPI.accent,
  reentrada: KPI.danger,
  garantia: KPI.primary,
};

/** Gráfico rosca: Chaves, Help Desk, Loc Solution, Reentrada */
export const ASSISTENCIA_DOUGHNUT_COLORS = [
  KPI.primary,
  KPI.secondary,
  KPI.accent,
  KPI.danger,
];

export const ASSISTENCIA_TOTAL_GRADIENT = `linear-gradient(135deg, ${KPI.primary}, ${KPI.accent})`;
